import Title from '../Title/Title';
import s from './Experience.module.scss';
import { Building, Calendar, ChevronDown, ExternalLink, File, Github } from 'lucide-react';
import { experiences, ExperienceType } from './consts';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import cn from 'classnames';
import { useMedia } from '../../hooks/useMedia';
import { Button } from '../Button/Button';


type ExperienceItemProps = {
  experience: ExperienceType;
  icon?: React.ReactNode;
  index: number;
  isInView: boolean;
};

export const ExperienceItem = ({ experience, icon, index, isInView }: ExperienceItemProps) => {
  const [isActive, setIsActive] = useState(false);
  const { isMobile } = useMedia();

  return (
    <motion.div className={cn(s.item, { [s.active]: isActive })}
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={() => setIsActive(!isActive)}
    >
      <div className={s.content}>
        <h3 className={s.title}>
          {isMobile && experience.mobileTitle
            ? experience.mobileTitle
            : experience.title}
        </h3>
        <div className={s.extraInfo}>
          {experience.organization && <div className={s.organization}>
            <span><Building size={16} /></span>
            <p>{isMobile && experience.mobileOrganization
              ? experience.mobileOrganization
              : experience.organization}</p>
          </div>}
          {experience.period && (
            <div className={s.period}>
              <span><Calendar size={16} /></span>
              <p>{experience.period}</p>
            </div>
          )}
        </div>
        <p className={s.position}>
          {experience.position}
        </p>
        <div className={cn(s.chevron, { [s.active]: isActive })}><ChevronDown /></div>
        {isActive && (<>
          <div className={s.separator}></div>
          <div className={s.description}>
            {experience.image_src &&
              <div className={s.description_img}><img src={experience.image_src} alt={experience.title} /></div>}
            <div className={s.description_info}>
              <p>{experience.description}</p>

              {(experience.link || experience.github || experience.documentLink) &&
                <div className={s.links}>
                  {experience.link &&
                    <Button text={'Visit'} variant={'secondary'} component={'a'} href={experience.link}
                      icon={<ExternalLink size={16} />} />}
                  {experience.github &&
                    <Button text={'Github'} variant={'secondary'} component={'a'} href={experience.github}
                      icon={<Github size={16} />} />}
                  {experience.documentLink &&
                    <Button text={'Document'} variant={'secondary'} component={'a'} href={experience.documentLink}
                      icon={<File size={16} />} />}

                </div>
              }
            </div>
          </div>
        </>)}
      </div>
    </motion.div>
  );
};

export default function Experience() {
  const [selectedSwitcher, setSelectedSwitcher] = useState<string>('business');
  const filteredExperiences = experiences.find((exp) =>
    exp.type === selectedSwitcher)?.experiences || [];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="experience">
      <motion.div
        ref={ref}
        className={s.root_wrapper}
        initial={{ opacity: 0, y: 12 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className={s.root}>
          <Title
            text="Projects & Experience"
            size="large"
            level={2}
            className={s.header_title}
          />
          <p className={s.title_desc}>Examples of my work, ranging from commercial projects to personal
            experiments</p>

          <div className={s.switchers}>
            <button
              className={cn(s.switcher, { [s.active]: selectedSwitcher === 'educational' })}
              onClick={() => setSelectedSwitcher('educational')}>Educational
            </button>
            <button className={cn(s.switcher, { [s.active]: selectedSwitcher === 'business' })}
              onClick={() => setSelectedSwitcher('business')}>Business
            </button>
            <button className={cn(s.switcher, { [s.active]: selectedSwitcher === 'personal' })}
              onClick={() => setSelectedSwitcher('personal')}>Personal
            </button>
          </div>

          <div className={s.experiences}>
            {filteredExperiences.map((experience, index) => (
              <ExperienceItem
                key={experience.title + experience.description + experience.period}
                experience={experience}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
